import { useEffect } from "react";
import { site } from "@/config/site";

// Absolute-URL rule: canonical / og:url / og:image / JSON-LD url+image are only
// emitted once site.domain holds the confirmed production domain. Until then
// they are omitted on purpose — a guessed canonical silently de-indexes a site.

function upsert(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    if (el && el.dataset.managed === "seo") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.dataset.managed = "seo";
}

const SHARE_IMAGE = "/og-image.png"; // generated brand card in public/

export function SEO({ title, description, path = "/", type = "website", image, jsonLd = [] }) {
  useEffect(() => {
    const url = site.domain ? `${site.domain}${path}` : "";
    const imageUrl = site.domain ? (image || `${site.domain}${SHARE_IMAGE}`) : "";
    document.title = title;
    upsert("name", "description", description);
    upsert("property", "og:title", title);
    upsert("property", "og:description", description);
    upsert("property", "og:type", type);
    upsert("property", "og:site_name", site.name);
    upsert("property", "og:url", url);
    upsert("property", "og:image", imageUrl);
    upsert("name", "twitter:card", imageUrl ? "summary_large_image" : "summary");
    upsert("name", "twitter:title", title);
    upsert("name", "twitter:description", description);
    upsert("name", "twitter:image", imageUrl);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (url) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", url);
    } else if (canonical) {
      canonical.remove();
    }

    const scripts = jsonLd.filter(Boolean).map((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.managed = "seo";
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => s.remove());
  }, [title, description, path, type, image, jsonLd]);
  return null;
}

export function localBusinessLd() {
  const address = {
    "@type": "PostalAddress",
    addressLocality: site.address.city,
    addressRegion: site.address.province,
    addressCountry: site.address.country,
  };
  if (site.showAddress) {
    address.streetAddress = site.address.street;
    address.postalCode = site.address.postal;
  }
  const ld = {
    "@context": "https://schema.org",
    "@type": "HousePainter",
    name: site.name,
    telephone: site.phone,
    email: site.email,
    address,
    areaServed: ["Delta BC", "Ladner BC", "Tsawwassen BC", "Surrey BC"],
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
  };
  if (site.domain) {
    ld.url = site.domain;
    ld.image = `${site.domain}${SHARE_IMAGE}`;
  }
  return ld;
}

export function serviceLd(service) {
  const ld = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    serviceType: service.name,
    provider: { "@type": "HousePainter", name: site.name, telephone: site.phone },
    areaServed: ["Delta BC", "Ladner BC", "Tsawwassen BC", "Surrey BC"],
    description: service.short,
  };
  if (site.domain) ld.url = `${site.domain}/services/${service.slug}`;
  return ld;
}

export function faqLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function breadcrumbLd(items) {
  if (!site.domain) return null; // item URLs require the confirmed domain
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${site.domain}${item.path}`,
    })),
  };
}

export function articleLd(post, imageUrl) {
  const ld = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: { "@type": "Organization", name: site.name },
    publisher: { "@type": "Organization", name: site.name },
  };
  if (site.domain) {
    ld.mainEntityOfPage = `${site.domain}/blog/${post.slug}`;
    ld.image = imageUrl;
  }
  return ld;
}
